"use client"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Layout from "../../components/Layout"
import { toast } from "react-toastify"
import axios from "axios"


const DownloadPage = () => {
    const [isMobile, setIsMobile] = useState(false)
    const [isTablet, setIsTablet] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const navigate = useNavigate()
    const [showPrayerModal, setShowPrayerModal] = useState(false)
    const [pdfFiles, setPdfFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const PDFIcon = () => (
        <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ color: "#dc2626" }}
        >
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            <path d="M10.5,13.5L11.5,14.5L10.5,15.5V17H9V15.5L8,14.5L9,13.5V12H10.5V13.5M12.5,12V17H14V15H15.5V14H14V13H15.5V12H12.5M16.5,12V17H18V15.5H19.5V14.5H18V13.5H19.5V12.5H18V12H16.5Z" />
        </svg>
    );


    const DownloadButtonIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
        </svg>
    );

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth
            setIsMobile(width <= 768)
            setIsTablet(width > 768 && width <= 1024)
        }

        checkScreenSize()
        window.addEventListener("resize", checkScreenSize)

        return () => {
            window.removeEventListener("resize", checkScreenSize)
        }
    }, [navigate])

    // Fetch available PDF files
    const fetchPDFFiles = async () => {
        try {
            setIsLoading(true);
            const response = await fetch("/api/downloads/pdf-list");
            if (response.ok) {
                const data = await response.json();
                console.log("PDF API response:", data);
                setPdfFiles(data.data?.pdfs || []);
            } else {
                console.error("Failed to fetch PDF files");
            }
        } catch (error) {
            console.error("Error fetching PDF files:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPDFFiles();

    }, []);

    const handleDownload = async (pdfUrl, filename) => {
        try {
            if (!pdfUrl) {
                toast.error("PDF URL not available for this form.");
                return;
            }

            // Extract the S3 path from the full URL
            // URL format: https://templestorage.s3.eu-west-2.amazonaws.com/editable-pdfs/filename.pdf
            const s3PathMatch = pdfUrl.match(/amazonaws\.com\/(.+)$/);
            if (!s3PathMatch) {
                toast.error("Invalid PDF URL format.");
                return;
            }
            const s3Path = s3PathMatch[1];

            // Download via existing S3 proxy to avoid CORS issues
            const response = await fetch(`/api/s3-proxy/${s3Path}`);
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.style.display = "none";
                a.href = url;
                a.download = filename || "form.pdf";
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                toast.success("Download started successfully!");
            } else {
                toast.error("Failed to download file. Please try again.");
            }
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Failed to download file. Please try again.");
        }
    };

    const getCategoryFromFilename = (filename) => {
        if (filename.includes("Homa Request")) return "Homa Requests";
        if (filename.includes("Merit Transfer")) return "Merit Transfer";
        if (filename.includes("Seasonal")) return "Seasonal Forms";
        return "Other Forms";
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case "Homa Requests":
                return "#7fb3a3";
            case "Merit Transfer":
                return "#6366f1";
            case "Seasonal Forms":
                return "#f59e0b";
            default:
                return "#6b7280";
        }
    };

    const breadcrumb = (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "0.9rem" }}>
            <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/home")}
            >
                <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
            </svg>
            <span>/</span>
            <span style={{ cursor: "pointer" }} onClick={() => navigate("/home")}>Home</span>
            <span>/</span>
            <span>Download Prayer Forms</span>
        </div>
    )

    const getSidebarWidth = () => {
        if (isMobile) return "80%"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentMargin = () => {
        if (isMobile) return "0"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentWidth = () => {
        if (isMobile) return "100%"
        return sidebarCollapsed ? "calc(100% - 80px)" : "calc(100% - 250px)"
    }

    const styles = {
        mainContent: {
            marginLeft: getMainContentMargin(),
            paddingTop: "80px",
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            width: getMainContentWidth(),
            transition: "margin-left 0.3s ease, width 0.3s ease",
        },
        breadcrumb: {
            display: "flex",
            alignItems: "center",
            marginBottom: "30px",
            color: "#6b7280",
            fontSize: "0.9rem",
            gap: "8px",
        },
        cardsGrid: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "20px",
            width: "100%",
            marginTop: "30px",
        },
        card: {
            backgroundColor: "#7fb3a3",
            borderRadius: "12px",
            padding: "10px",
            textAlign: "center",
            color: "white",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            width: "90%",
            margin: "0 auto",
            marginTop: "20px",
            transition: "all 0.3s ease",
        },
        cardIcon: {
            fontSize: "3rem",
            marginBottom: "20px",
            opacity: 0.9,
            display: "flex",
            justifyContent: "center",
        },
        cardTitle: {
            fontSize: "1.3rem",
            fontWeight: "600",
            marginBottom: "15px",
            lineHeight: "1.3",
        },
        cardDescription: {
            fontSize: "0.95rem",
            opacity: 0.9,
            lineHeight: "1.4",
        },
        error: {
            color: "#ef4444",
            fontSize: "0.9rem",
            textAlign: "center",
            marginBottom: "20px",
        },

        pageTitle: {
            fontSize: "2rem",
            fontWeight: "600",
            color: "#1f2937",
            marginBottom: "8px",
        },
        pageSubtitle: {
            fontSize: "1.1rem",
            color: "#6b7280",
            marginBottom: "40px",
            marginTop: "40px",
        },
        loadingContainer: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            fontSize: "1.1rem",
            color: "#6b7280",
        },
        gridContainer: {
            display: "grid",
            gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "24px",
            marginTop: "20px",
        },
        pdfCard: {
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            transition: "all 0.3s ease",
            border: "1px solid #e5e7eb",
            height: "280px",
            display: "flex",
            flexDirection: "column",
        },
        pdfCardHover: {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
        },
        pdfCardHeader: {
            padding: "20px",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            height: "160px",
        },
        pdfIconContainer: {
            padding: "12px",
            borderRadius: "8px",
            backgroundColor: "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        pdfDetails: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
        pdfTitle: {
            fontSize: "1.1rem",
            fontWeight: "600",
            color: "#1f2937",
            marginBottom: "4px",
            lineHeight: "1.4",
        },
        categoryBadge: {
            display: "inline-block",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "500",
            color: "white",
            marginBottom: "8px",
        },
        pdfSize: {
            fontSize: "0.875rem",
            color: "#6b7280",
        },
        pdfCardBody: {
            padding: "20px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
        downloadButton: {
            width: "100%",
            backgroundColor: "#7fb3a3",
            color: "white",
            border: "none",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "1rem",
            fontWeight: "500",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            transition: "background-color 0.2s",
        },
        downloadButtonHover: {
            backgroundColor: "#6a9b8e",
        },
        emptyState: {
            textAlign: "center",
            padding: "60px 20px",
            color: "#6b7280",
        },
        emptyStateIcon: {
            fontSize: "4rem",
            marginBottom: "16px",
            opacity: 0.5,
        },
        emptyStateTitle: {
            fontSize: "1.5rem",
            fontWeight: "600",
            marginBottom: "8px",
        },
        emptyStateText: {
            fontSize: "1rem",
            lineHeight: "1.5",
        },
    }

    return (
        <Layout
            activeMenuItem="Download Prayer Forms"
            breadcrumb={breadcrumb}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
        >
            {/* Toast notifications will be shown via react-toastify */}


            {/*<h1 style={styles.pageTitle}>Download Prayer Forms</h1>*/}
            <p style={styles.pageSubtitle}>
                Download editable PDF forms for your prayer requests. Simply click the
                download button to save the form to your device.
            </p>

            {isLoading ? (
                <div style={styles.loadingContainer}>
                    <div>Loading PDF forms...</div>
                </div>
            ) : pdfFiles.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={styles.emptyStateIcon}>📄</div>
                    <h2 style={styles.emptyStateTitle}>No PDF Forms Available</h2>
                    <p style={styles.emptyStateText}>
                        There are currently no PDF forms available for download. Please
                        check back later.
                    </p>
                </div>
            ) : (
                <div style={styles.gridContainer}>
                    {pdfFiles.map((file, index) => {
                        const category = file.category;
                        const categoryColor = getCategoryColor(category);

                        return (
                            <div
                                key={index}
                                style={styles.pdfCard}
                                onMouseEnter={(e) => {
                                    Object.assign(e.currentTarget.style, styles.pdfCardHover);
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0px)";
                                    e.currentTarget.style.boxShadow =
                                        "0 4px 6px rgba(0, 0, 0, 0.1)";
                                }}
                            >
                                <div style={styles.pdfCardHeader}>
                                    <div style={styles.pdfIconContainer}>
                                        <PDFIcon />
                                    </div>
                                    <div style={styles.pdfDetails}>
                                        <div
                                            style={{
                                                ...styles.categoryBadge,
                                                backgroundColor: categoryColor,
                                            }}
                                        >
                                            {category}
                                        </div>
                                        <h3 style={styles.pdfTitle}>{file.displayName}</h3>
                                        <p style={styles.pdfSize}>
                                            PDF Document • {(file.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                </div>
                                <div style={styles.pdfCardBody}>
                                    <button
                                        style={styles.downloadButton}
                                        onClick={() => handleDownload(file.pdfUrl, file.filename)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                                styles.downloadButtonHover.backgroundColor;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = "#7fb3a3";
                                        }}
                                    >
                                        <DownloadButtonIcon />
                                        Download PDF
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

        </Layout>
    )
}

export default DownloadPage